/**
 * Subscriptions API slice
 *
 * Enhances generated RTK Query endpoints with cache tags and exports user-friendly hook names.
 */

import { enhancedApi } from '@store/generated/subscriptions';

/**
 * Feature-specific tag types for subscriptions cache management
 */
const SUBSCRIPTIONS_TAG_TYPES = [
  'Subscription',
  'BundleCatalog',
  'UserBundles',
  'Entitlements',
  'PaymentMethod',
  'BillingInfo',
  'BillingInterval',
  'Invoices',
  'SubscriptionDetails',
  'UserInfo',
] as const;

/**
 * Subscriptions API - enhanced from generated endpoints with cache tags
 */
export const subscriptionsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: SUBSCRIPTIONS_TAG_TYPES,
  endpoints: {
    // Provider endpoints (read-only)
    getV2ProviderSubscription: { providesTags: ['Subscription'] },
    getFullSubscriptionDetails: {
      providesTags: ['Subscription', 'BillingInfo', 'BillingInterval', 'PaymentMethod'],
    },
    getV2ProviderBundles: { providesTags: ['BundleCatalog'] },
    getV2ProviderSubscriptionBundles: {
      providesTags: result =>
        result?.bundles
          ? [
              ...result.bundles.map(bundle => ({
                type: 'UserBundles' as const,
                id: bundle.bundle_id,
              })),
              { type: 'UserBundles' as const, id: 'LIST' },
            ]
          : [{ type: 'UserBundles' as const, id: 'LIST' }],
    },
    getV2ProviderEntitlements: { providesTags: ['Entitlements'] },

    // Invoices endpoint
    getV2ProviderSubscriptionInvoices: { providesTags: ['Invoices'] },

    // Payment method endpoints
    getV2ProviderSubscriptionPaymentMethod: { providesTags: ['PaymentMethod'] },
    putV2ProviderSubscriptionPaymentMethod: {
      invalidatesTags: [
        'PaymentMethod',
        'Subscription',
        'Invoices',
        'SubscriptionDetails',
        'UserInfo',
      ],
    },
    postV2ProviderSubscriptionSetupIntent: {},

    // Subscription management endpoints
    postV2ProviderSubscriptionCancel: {
      invalidatesTags: ['Subscription'],
    },
    postV2ProviderSubscriptionReactivate: {
      invalidatesTags: ['Subscription'],
    },

    // Billing info endpoints
    getV2ProviderSubscriptionBillingInfo: { providesTags: ['BillingInfo'] },
    putV2ProviderSubscriptionBillingInfo: {
      invalidatesTags: ['BillingInfo'],
    },

    // Billing interval endpoints
    getV2ProviderSubscriptionBillingInterval: { providesTags: ['BillingInterval'] },
    putV2ProviderSubscriptionBillingInterval: {
      invalidatesTags: ['BillingInterval', 'Subscription', 'Invoices'],
    },
  },
});

// Export hooks with user-friendly names
export const {
  useGetV2ProviderSubscriptionQuery: useGetSubscriptionQuery,
  useGetFullSubscriptionDetailsQuery: useGetFullSubscriptionDetailsQuery,
  useGetV2ProviderBundlesQuery: useGetBundleCatalogQuery,
  useGetV2ProviderSubscriptionBundlesQuery: useGetUserBundlesQuery,
  useGetV2ProviderEntitlementsQuery: useGetEntitlementsQuery,

  // Invoices hook
  useGetV2ProviderSubscriptionInvoicesQuery: useGetInvoicesQuery,

  // Payment method hooks
  useGetV2ProviderSubscriptionPaymentMethodQuery: useGetPaymentMethodQuery,
  usePutV2ProviderSubscriptionPaymentMethodMutation: useUpdatePaymentMethodMutation,
  usePostV2ProviderSubscriptionSetupIntentMutation: useCreateSetupIntentMutation,

  // Subscription management hooks
  usePostV2ProviderSubscriptionCancelMutation: useCancelSubscriptionMutation,
  usePostV2ProviderSubscriptionReactivateMutation: useReactivateSubscriptionMutation,

  // Billing info hooks
  useGetV2ProviderSubscriptionBillingInfoQuery: useGetBillingInfoQuery,
  usePutV2ProviderSubscriptionBillingInfoMutation: useUpdateBillingInfoMutation,

  // Billing interval hooks
  useGetV2ProviderSubscriptionBillingIntervalQuery: useGetBillingIntervalQuery,
  usePutV2ProviderSubscriptionBillingIntervalMutation: useUpdateBillingIntervalMutation,
} = subscriptionsApi;

// Re-export invoice types for convenience
export type {
  SubscriptionInvoice,
  SubscriptionInvoicesResponse,
} from '@store/generated/subscriptions';
